/**
 * Shared empty-state and portal copy (Phase 2 / Batch 5).
 * Import these instead of inline strings to avoid drift across roles.
 */
export const PORTAL_EMPTY_STATES = {
  tenant: {
    noActiveLease: 'No active lease on file.',
    noPaymentMethod: 'No payment methods added',
    noOpenCharges: 'No open charges to pay. Contact your property manager if your balance looks wrong.',
    noPaymentHistory: 'No payment history found.',
    loadingPaymentPortal: 'Loading payment portal...',
    noDocuments: 'No documents found',
    noDocumentsFiltered: 'Try adjusting your filters',
    noDocumentsUpload: 'Upload your first document to get started',
    noCommunityAnnouncements: 'No community announcements available.',
    noRecentActivity: 'No recent activity found.',
  },
  resident: {
    portalSourceLabel: 'Resident',
    portalSourceTitle: 'Resident portal',
    maintenanceCompletedHint: 'Your completed maintenance requests',
    maintenanceActiveHint: 'Submit a request and track its status with your property team',
    messageButton: 'Message resident',
    notFound: 'Resident not found',
    loadingProfile: 'Loading resident profile...',
    noUnitAssigned: 'No resident assigned',
    workOrderCreatedBy: 'Work order created by resident',
    portalLinkLabel: 'Resident Portal',
  },
  vendor: {
    noJobs: 'No active jobs assigned.',
    noJobsFiltered: 'No work orders match your current filters.',
    noJobsTitle: 'No active work orders',
    noJobsDashboardTitle: 'No jobs found',
    noJobsDashboardFiltered: 'Try adjusting your filters to see more jobs.',
    loadingJobDetails: 'Loading job details…',
    invoiceEmptyCompleted: 'This job is complete but no invoice was created.',
    invoiceEmptyPending: 'Create an invoice when you complete this job.',
    noPayouts: 'No payout history yet.',
    noInvoices: 'No invoices found',
    noPayments: 'No payments found',
    noPortfolioItems: 'No portfolio items yet',
    noServicesListed: 'No services listed',
  },
  pm: {
    gettingStartedHint: 'Get started by adding your first property to manage units, leases, and payments.',
    noOccupancyUntilUnits: 'Add units to track occupancy for this property.',
    rentChargesTitleNone: 'No Rent Charges Yet',
    rentChargesMessageNone:
      'Rent charges are created automatically on the 1st of each month. You can also create custom charges for fees, deposits, or repairs.',
    rentChargesTitleFiltered: 'No Matches for Current Filters',
    rentChargesTitleSearch: 'No Rent Charges Found',
    rentChargesMessageSearch: 'No charges match your search. Try a different search term.',
    residentDirectoryTitle: 'Resident Directory',
    residentDirectorySubtitle: 'Manage resident information, leases, and communications.',
    residentColumn: 'Resident',
    addResident: 'Add Resident',
    addResidentModalTitle: 'Add New Resident',
    createResident: 'Create Resident',
    quickFilterCurrentResidents: 'Current Residents',
    quickFilterPastResidents: 'Past Residents',
    noResidentsYetTitle: 'No Residents Yet',
    noResidentsYetMessage:
      'Get started by adding your first resident. You can add their lease information, contact details, and property assignment.',
    noResidentsFoundTitle: 'No Residents Found',
    noResidentsSearchMessage: 'No residents match your search. Try a different search term.',
    deleteResidentsTitle: 'Delete Residents',
    deleteResidentTitle: 'Delete Resident',
    deleteResidentMessage:
      'Are you sure you want to delete this resident? This will also remove related data. This action cannot be undone.',
    loadResidentsError: 'Failed to load residents. Please try again.',
    residentsExportedCsv: 'Residents exported as CSV',
    residentsExportedPdf: 'Residents exported as PDF',
    leaseResidentColumn: 'Resident',
    viewResidentProfile: 'View Resident Profile',
    signaturePartyResident: 'Resident',
    occupancyReportResidentColumn: 'Resident',
    currentResidentsSection: 'Current Residents',
    billsTitleNone: 'No Bills Yet',
    billsMessageNone:
      'Vendor bills appear here when you receive invoices for repairs, utilities, or services. Create a bill to start tracking expenses.',
    billsTitleFiltered: 'No Matches for Current Filters',
    billsTitleSearch: 'No Bills Found',
    billsMessageSearch: 'No bills match your search. Try a different search term.',
    ledgerTitleProcessing: 'Processing Transactions...',
    ledgerTitleNone: 'No Ledger Entries',
    ledgerMessageProcessing:
      'Journal entries are being generated from recent activity. This may take a moment.',
    ledgerMessageFiltered: 'No entries match your filters. Try adjusting date range or account filters.',
    ledgerMessageNone: 'Ledger entries will appear when you pay bills or record rent payments.',
    ownersNotFoundTitle: 'No Owners Found',
    ownersNotFoundMessage:
      'No owners match your current filters. Add your first owner to start managing property ownership.',
  },
  maintenance: {
    loadError: 'Failed to load work orders. Please try again.',
    noFiltered: 'No work orders match your current filters.',
    noProperty: 'No maintenance work orders found for this property.',
    auditLoggedOnWorkOrder: 'logged on the work order',
    messageSubjectPrefix: 'Update on work order',
  },
  owner: {
    noDistributions: 'No distributions to acknowledge yet.',
    noOwnership: 'No properties linked to your account yet.',
    noStatements: 'No statements available for this period.',
    noDocuments: 'No documents found',
    noDocumentsFiltered: 'Try adjusting your filters',
    noDocumentsUpload: 'Upload your first document to get started',
    noMessages: 'No messages yet',
    noMessagesCta: 'Start a conversation with your property manager',
    noProperties: 'No properties found',
    noDocumentsForProperty: 'No documents available for this property',
    noDocumentsForPropertyFiltered: 'No documents found matching your filters',
  },
  investor: {
    noDocuments: 'No documents found',
    noDocumentsFiltered: 'Try adjusting your filters',
    noPortfolioSearch: 'No properties match your search.',
    noPortfolio: 'No investments found. Browse opportunities to get started.',
    noMessages: 'No messages yet',
  },
  report: {
    fieldResident: 'Resident',
    dataSourceResidents: 'Residents',
  },
  filter: {
    residentLabel: 'Resident',
    allResidents: 'All Residents',
  },
  command: {
    categoryResidents: 'Residents',
  },
  document: {
    entityTypeResident: 'Resident',
  },
  lease: {
    noLeasesYetTitle: 'No Leases Yet',
    noAgreementsMessage:
      'Get started by creating your first lease. You can track lease terms, renewals, and resident agreements.',
    noMatchesFiltersTitle: 'No Matches for Current Filters',
    noMatchesFiltersMessage:
      'leases exist, but none match your current search or filters. Try adjusting your criteria.',
    noLeasesSearchTitle: 'No Leases Found',
    noLeasesSearchMessage: 'No leases match your search. Try a different search term.',
    createLeaseAction: 'Create Lease',
    relatedEntitiesSection: 'Related Entities',
    sendSheetDescription: 'Email the resident a DocuSign link for electronic signature',
    sendSheetSubtitle: 'Emails the resident a DocuSign link',
    sendInfoPrimary: 'The lease will be sent to the resident for electronic signature via DocuSign.',
    sendInfoSecondary:
      'Once signed, the onboarding checklist will be automatically created and the resident will receive a welcome email.',
    detailsResidentLabel: 'Resident:',
    unknownResident: 'Unknown Resident',
    unknownProperty: 'Unknown Property',
    unknownUnit: 'Unknown Unit',
    residentEmailLabel: 'Resident Email Address',
    residentEmailPlaceholder: 'resident@example.com',
    previewResidentRole: 'Resident',
    terminateMayAffectRecords:
      'Terminating this lease will mark it as terminated and may affect resident records.',
  },
} as const;
